import React, { useCallback, useEffect, useState } from 'react';
import { LogOut, Plus, Gift, Truck, Printer, CheckCircle2, Pencil } from 'lucide-react';
import { ensureOrderCoords } from './geocode';
import { CABIFY_STATE_ES } from './CabifyBatch';
import { supabase } from './supabase';
import { NewOrder } from './NewOrder';
import { DataGrid, GridCol } from './ui/DataGrid';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { PageHeader, Notice } from './costs/plata-kit';

// Pantalla del usuario de rol 'canje' (Rafa, 13/10/2026): solo carga pedidos de CANJE para
// creadores de contenido y ve los que ya se cargaron. No ve el resto del admin.
// Un canje es una orden normal para el taller, con total $0, sin cobro y sin WhatsApp
// (ver NewOrder `canje` y migr 106).

type CanjeRow = {
    id: number; created_at: string; contact_name: string | null; canje_phone: string | null;
    products: any[] | null; status: string; delivery_type: string | null; delivery_address: string | null; ready_by: string | null;
    delivery_unit: string | null; delivery_lat: number | null; delivery_lon: number | null;
};
type Parcel = { order_id: number; parcel_id: string; status: string };

const STATUS_LABEL: Record<string, string> = {
    nueva: 'En cola', falta_insumo: 'Falta insumo', cortado: 'En producción', empaquetado: 'Listo',
    entregado: 'Entregado', cancelada: 'Cancelado',
};
const fmtDate = (d: string | null) => d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

export function CanjeHome({ email }: { email?: string }) {
    const [rows, setRows] = useState<CanjeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [msg, setMsg] = useState<{ text: string; tone: 'success' | 'danger' } | null>(null);
    const [parcels, setParcels] = useState<Map<number, Parcel[]>>(new Map());
    const [busy, setBusy] = useState<number | null>(null);
    const [editing, setEditing] = useState<any | null>(null);

    const load = useCallback(async () => {
        const { data } = await supabase.from('orders')
            .select('id, created_at, contact_name, canje_phone, products, status, delivery_type, delivery_address, ready_by, delivery_unit, delivery_lat, delivery_lon')
            .eq('is_canje', true).is('deleted_at', null)
            .order('created_at', { ascending: false }).limit(300);
        const list = (data || []) as CanjeRow[];
        setRows(list);
        // etiquetas Cabify de los canjes (las borradas no cuentan)
        const ids = list.map(r => r.id);
        const m = new Map<number, Parcel[]>();
        if (ids.length) {
            const { data: ps } = await supabase.from('cabify_deliveries').select('order_id, parcel_id, status')
                .in('order_id', ids).neq('status', 'deleted');
            for (const p of (ps || []) as Parcel[]) { if (!m.has(p.order_id)) m.set(p.order_id, []); m.get(p.order_id)!.push(p); }
        }
        setParcels(m);
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    // ── Cabify: mismo flujo que la card del admin (App.tsx cabifyGenerate). Crear la etiqueta NO
    //    despacha ni cobra: el envío sale cuando se despacha (portal de Cabify / "Despachar todos"). ──
    const openLabels = async (ids: string[]) => {
        const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'labels', parcel_ids: ids } });
        if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo bajar la etiqueta');
        const bytes = atob(data.pdf_base64); const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        window.open(URL.createObjectURL(new Blob([arr], { type: 'application/pdf' })), '_blank');
    };
    const generateLabel = async (r: CanjeRow) => {
        setBusy(r.id); setMsg(null);
        try {
            const geo = await ensureOrderCoords(r as any);
            if (!geo.ok) {
                setMsg({ tone: 'danger', text: `Revisá la dirección: ${geo.reason}${geo.found ? `. Google la entendió como: ${geo.found}` : ''}. Tocá "Editar" y elegila del autocompletado.` });
                return;
            }
            const { data, error } = await supabase.functions.invoke('cabify-logistics', {
                body: {
                    action: 'create_batch', created_by: email,
                    orders: [{ order_id: r.id, order_display: `K-${r.id}`, dropoff_addr: r.delivery_address, dropoff_lat: geo.lat, dropoff_lon: geo.lon, dropoff_name: r.contact_name, dropoff_phone: r.canje_phone, instr: r.delivery_unit ? `Piso/depto: ${r.delivery_unit}` : undefined }],
                },
            });
            const res = data?.results?.[0];
            if (res && !res.ok && Array.isArray(res.existing) && res.existing.length) { await openLabels(res.existing); await load(); return; }
            if (error || !data?.ok || !res?.ok) throw new Error(res?.error || data?.error || error?.message || 'No se pudo generar la etiqueta');
            await openLabels(res.parcel_ids || [res.parcel_id]);
            setMsg({ tone: 'success', text: res.bultos > 1 ? `${res.bultos} etiquetas Cabify generadas` : 'Etiqueta Cabify generada. Imprimila y pegala en el paquete.' });
            await load();
        } catch (e: any) {
            setMsg({ tone: 'danger', text: 'Cabify: ' + (e?.message || e) });
        } finally { setBusy(null); }
    };
    const printLabel = async (r: CanjeRow) => {
        setBusy(r.id);
        try { await openLabels((parcels.get(r.id) || []).map(p => p.parcel_id)); }
        catch (e: any) { setMsg({ tone: 'danger', text: 'Cabify: ' + (e?.message || e) }); await load(); }
        finally { setBusy(null); }
    };
    const markDelivered = async (r: CanjeRow) => {
        if (!window.confirm(`¿Marcar el canje K-${r.id} (${r.contact_name || ''}) como entregado?`)) return;
        setBusy(r.id);
        const { error } = await supabase.from('orders').update({ status: 'entregado' }).eq('id', r.id).eq('is_canje', true);
        setBusy(null);
        if (error) { setMsg({ tone: 'danger', text: 'No se pudo marcar: ' + error.message }); return; }
        setRows(rs => rs.map(x => x.id === r.id ? { ...x, status: 'entregado' } : x));
    };
    const openEdit = async (r: CanjeRow) => {
        const { data } = await supabase.from('orders').select('*').eq('id', r.id).single();
        if (data) setEditing(data);
    };

    const cols: GridCol<CanjeRow>[] = [
        { key: 'id', header: 'Pedido', sort: r => r.id, render: r => <span className="font-semibold">K-{r.id}</span> },
        { key: 'fecha', header: 'Cargado', sort: r => r.created_at, render: r => <span className="tabular-nums">{fmtDate(r.created_at)}</span> },
        { key: 'creador', header: 'Creador', sort: r => r.contact_name || '', render: r => <span className="font-semibold text-ink">{r.contact_name || '—'}</span> },
        {
            key: 'productos', header: 'Productos', sort: r => (r.products || []).map(p => p.name).join(' '), render: r => (
                <span className="grid">{(r.products || []).map((p, i) => <span key={i} className="text-soft">{p.quantity || 1}× {p.name}</span>)}</span>
            ),
        },
        { key: 'entrega', header: 'Entrega', hideOnMobile: true, render: r => <span className="text-soft">{r.delivery_type === 'envio' ? (r.delivery_address || 'Envío') : 'Retira en el showroom'}</span> },
        { key: 'listo', header: 'Listo para', sort: r => r.ready_by || '', hideOnMobile: true, render: r => <span className="tabular-nums">{fmtDate(r.ready_by)}</span> },
        {
            key: 'estado', header: 'Estado', sort: r => r.status, render: r => (
                <Badge size="sm" tone={r.status === 'entregado' ? 'success' : r.status === 'cancelada' ? 'danger' : r.status === 'empaquetado' ? 'info' : 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
            ),
        },
        {
            key: 'acciones', header: '', align: 'right', render: r => {
                const ps = parcels.get(r.id) || [];
                const isEnvio = r.delivery_type === 'envio';
                return (
                    <div className="flex flex-wrap justify-end gap-1.5">
                        {isEnvio && r.status !== 'cancelada' && (ps.length
                            ? <>
                                <span className="self-center text-[length:var(--text-xs)] text-soft" title="Estado del envío en Cabify">{CABIFY_STATE_ES[ps[0].status] || ps[0].status}</span>
                                <Button variant="secondary" size="sm" onClick={() => printLabel(r)} loading={busy === r.id}><Printer size={14} /> Etiqueta{ps.length > 1 ? `s (${ps.length})` : ''}</Button>
                            </>
                            : <Button variant="secondary" size="sm" onClick={() => generateLabel(r)} loading={busy === r.id} disabled={!r.delivery_address}
                                title="Crea la etiqueta de Cabify y la abre para imprimir. No despacha ni cobra."><Truck size={14} /> Etiqueta Cabify</Button>)}
                        {r.status !== 'entregado' && r.status !== 'cancelada' && (
                            <Button variant="ghost" size="sm" onClick={() => markDelivered(r)} disabled={busy === r.id}><CheckCircle2 size={14} /> Entregado</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} aria-label="Editar canje" title="Editar (dirección, productos…)"><Pencil size={14} /></Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="tw min-h-screen bg-[var(--background)]">
            <header className="flex items-center justify-between border-b border-line bg-card px-4 py-3 sm:px-6">
                <img src="/logo.png" alt="Krilic" style={{ height: 22 }} />
                <div className="flex items-center gap-3">
                    <span className="hidden text-[length:var(--text-sm)] text-soft sm:inline">{email}</span>
                    <Button variant="ghost" size="sm" onClick={() => supabase.auth.signOut({ scope: 'local' })}><LogOut size={14} /> Salir</Button>
                </div>
            </header>
            <main className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-6">
                <PageHeader title="Canjes"
                    description="Pedidos para creadores de contenido. Entran al taller como cualquier pedido, pero no suman a facturación ni le mandan WhatsApp al creador."
                    actions={<Button onClick={() => setCreating(true)}><Plus size={16} /> Nuevo canje</Button>} />
                {msg && <div className="mb-4"><Notice tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Notice></div>}
                <DataGrid rows={rows} cols={cols} loading={loading} searchPlaceholder="Buscar creador o producto…"
                    emptyTitle="Todavía no hay canjes" emptyText="Cargá el primero con “Nuevo canje”."
                    emptyAction={<Button onClick={() => setCreating(true)}><Gift size={16} /> Nuevo canje</Button>} />
            </main>
            {editing && (
                <NewOrder canje initialOrder={editing}
                    onCancel={() => setEditing(null)}
                    onSuccess={() => { setEditing(null); setMsg({ text: 'Canje actualizado.', tone: 'success' }); load(); }}
                    showSnackbar={(m, t) => setMsg({ text: m, tone: t === 'error' ? 'danger' : 'success' })} />
            )}
            {creating && (
                <NewOrder canje
                    onCancel={() => setCreating(false)}
                    onSuccess={() => { setCreating(false); setMsg({ text: 'Canje cargado. Ya está en la cola del taller.', tone: 'success' }); load(); }}
                    showSnackbar={(m, t) => setMsg({ text: m, tone: t === 'error' ? 'danger' : 'success' })} />
            )}
        </div>
    );
}
